// ============================================================================
// The game clock: wall time -> in-game age mapping (GDD §3.1).
//
// One constant (msPerGameYear) drives every cadence; identical code path. The
// clock is a plain JSON-serializable value and NEVER reads Date.now() itself —
// wall time is an argument everywhere (migration contract #6: v1 trusts the
// client clock; v2 swaps in server time with no engine change).
//
// While running, age flows with the wall clock (so money accrues with the tab
// closed — the anchor persists). While paused (Step mode), age is fixed and
// advances only by explicit step actions.
// ============================================================================

export type TimeMode = 'step' | 'blitz' | 'fast' | 'live';

/** GDD §3.1 cadence table. Step has no wall-clock flow (manual advance). */
export const MS_PER_GAME_YEAR: Record<TimeMode, number | null> = {
  step: null,
  blitz: 60_000, // 1 game year = 60 s   (arc playtests, demos, P1)
  fast: 720_000, // 1 game year = 12 min (weekend feel-testing, P2)
  live: 604_800_000, // 1 game year = 1 real week (the real game)
};

export interface Clock {
  mode: TimeMode;
  /** true = age flows with wall time at the mode's rate */
  running: boolean;
  /** wall-clock ms at the anchor point */
  anchorWallMs: number;
  /** in-game age (years) at the anchor point */
  anchorAge: number;
}

export function newClock(startAge: number, wallMs: number): Clock {
  return { mode: 'step', running: false, anchorWallMs: wallMs, anchorAge: startAge };
}

/** The in-game age this clock reads at the given wall time. */
export function ageAt(clock: Clock, wallMs: number): number {
  const rate = MS_PER_GAME_YEAR[clock.mode];
  if (!clock.running || rate == null) return clock.anchorAge;
  return clock.anchorAge + Math.max(0, wallMs - clock.anchorWallMs) / rate;
}

/** Freeze the clock at its current reading (Step mode / pause button). */
export function pause(clock: Clock, wallMs: number): Clock {
  return { ...clock, anchorAge: ageAt(clock, wallMs), running: false };
}

/** Start (or re-anchor) flowing time in the given mode from the current age. */
export function run(clock: Clock, mode: TimeMode, wallMs: number): Clock {
  return { mode, running: true, anchorWallMs: wallMs, anchorAge: ageAt(clock, wallMs) };
}

/** Manual advance while paused (Step mode's +1 month / +1 year buttons). */
export function stepBy(clock: Clock, years: number, wallMs: number): Clock {
  return {
    ...clock,
    anchorAge: ageAt(clock, wallMs) + years,
    anchorWallMs: wallMs,
    running: false,
  };
}

/** Clamp a clock reading so a wildly wrong client clock cannot run past the guard. */
export function clampAge(age: number, maxAge: number): number {
  return Math.min(age, maxAge);
}

/**
 * Countdown formatting: "~3.2y" in game time, plus real time at the current
 * cadence ("· ~19 min real" in Blitz). Paused clock / Step mode have no
 * wall-clock rate, so only the game-time part renders. Lives here (not in the
 * component) so the boundary behaviour is unit-testable: months roll up to
 * "~1.0y" at 12, and the seconds branch hands over to minutes at exactly 1 min.
 */
export function eta(years: number, clock: Clock): string {
  const months = Math.max(1, Math.round(years * 12));
  const game = years >= 1 || months >= 12 ? `~${years.toFixed(1)}y` : `~${months}mo`;
  const rate = clock.running ? MS_PER_GAME_YEAR[clock.mode] : null;
  if (rate == null) return game;
  const ms = years * rate;
  const real =
    ms < 60_000
      ? `${Math.max(1, Math.round(ms / 1000))}s`
      : ms < 5_400_000
        ? `${Math.max(1, Math.round(ms / 60_000))} min`
        : ms < 172_800_000
          ? `${(ms / 3_600_000).toFixed(1)} h`
          : `${Math.round(ms / 86_400_000)} d`;
  return `${game} · ~${real} real`;
}
